import {Component, Event, EventEmitter, h, Listen, Method, Prop, State} from "@stencil/core";
import {UXContext} from "../../models/ux-context";
import {RawServerResponse} from "../../models/raw-server-response";
import {EError} from "../../core/error";
import * as api from "../../services/api";
import {IGeolocationInfo, IPlace} from "../../models/geolocation-info";
import {getCountryFromUrl, getLanguageFromUrl} from "../../services/i18n";

/**
 * This component redirects the user on page load to the local site
 */
@Component({
    tag: 'dc-redirect-modal',
    styleUrl: '../../assets/styles/style.scss',
    shadow: true,
})
export class DcRedirectModal {
    @Prop({mutable: true}) countrySwitchText = '';
    @Prop({mutable: true}) countrySwitchLinkText = 'Choose your country here.';
    @Prop({mutable: true}) description = 'For our products, services, solutions and local support teams, please choose your local site.\nFor more information on our company, please stay on the international site.';
    @Prop({mutable: true}) dialogTitle = 'Welcome to our international website';
    @Prop({mutable: true}) internationalButtonText = 'STAY ON INTERNATIONAL SITE';
    @Prop({mutable: true}) localButtonText = 'GO TO LOCAL SITE OF {0}';
    @Prop({mutable: true}) locationHasNoOwnSiteHeading = 'Unfortunately, there is no website for <span style="color:red">{0}</span>';
    @Prop({mutable: true}) locationHasNoOwnSiteDescription = 'We automatically selected the country which is responsible for you';
    @Prop({mutable: true}) locationSelectorLabel = 'PLEASE CHOOSE LOCATION';
    @Prop({mutable: true}) locationSelectorPlaceholder = '-- SELECT YOUR LOCATION --';
    @Prop({mutable: true}) regionSelectorLabel = 'PLEASE CHOOSE REGION';
    @Prop({mutable: true}) regionSelectorPlaceholder = '-- SELECT YOUR REGION --';
    @Prop() serverUrl?: string;
    @Prop({mutable: true}) show = false;

    @State() customerExperienceContext?: UXContext & RawServerResponse;
    @State() geoLocationInfo?: IGeolocationInfo;
    @State() closed = false;
    @State() delayedShow = false;
    @State() fetchDone = false;
    @State() finalLocalButtonText?;
    @State() selectedLocation?: string;
    @State() selectedRegion?: string;
    @State() shouldShowLocationSelector = false;
    @State() targetLocation?: string;

    @Event() goToLocalModalShareInformation : EventEmitter<{ countryOfDestination: string, languageOfDestination: string, keepSelection?: boolean, origin: 'welcome', triggerElement?: HTMLElement}>;


    constructor() {
        this.finalLocalButtonText = this.localButtonText.replace('{0}', 'YOUR COUNTRY');
        this.fetchGeolocationInfo()
            .catch((err) => {
                throw err;
            });
    }

    /**
     * Close the modal
     */
    @Method()
    public async close(fireEvent = true): Promise<void> {
        this.rememberModalWasDisplayed();
        this.closed = true;
        this.show = false;

        // todo: use central tracking library
        if (fireEvent) {
            const action = this.dialogTitle
                + "<>"
                + 'close';

            window.utag?.link({
                event_action: action.replace(/\s/g, "_").toLowerCase(),
                event_label: '',
                event_subtype: "onpage_flow",
                event_type: "click",
                event_reference: ""
            });
        }
    }

    /**
     * Get the customer experience object from the server, parse the response and update the modal view state
     */
    @Method()
    public async fetchGeolocationInfo(): Promise<IGeolocationInfo> {
        try {
            this.customerExperienceContext = await api.fetchCustomerExperienceContext({
                serverUrl: this.serverUrl ? new URL(this.serverUrl) : api.getServerUrl(),
            });
        } catch (err) {
            console.error(EError.GetCustomerExperienceContext);
        }

        try {
            this.geoLocationInfo = await api.fetchGeolocationInfo({
                serverUrl: this.serverUrl ? new URL(this.serverUrl) : api.getServerUrl(),
            });
        } catch (err) {
            throw EError.GetLocationInformation;
        }

        this.setRegion(this.geoLocationInfo.target_region);
        this.setLocation(this.geoLocationInfo.target_location);

        if (!this.selectedRegion) {
            this.shouldShowLocationSelector = true;
        }

        this.fetchDone = true;

        return this.geoLocationInfo;
    }

    @Method()
    public async getGeolocationInfo(): Promise<IGeolocationInfo> {
        return this.geoLocationInfo;
    }

    @Listen('locationChange')
    onLocationChange({detail}: CustomEvent<string>) {
        this.setLocation(detail);
    }

    @Listen('regionChange')
    onRegionChange({detail}: CustomEvent<string>) {
        this.setRegion(detail);
    }

    /**
     * Show the modal
     */
    @Method()
    public async open(): Promise<void> {
        this.closed = false;
        this.show = true;
    }

    /**
     * Inject the modal into the DOM
     */
    render() {
        if (!this.shouldShowModal()) {
            return;
        }

        setTimeout(() => {
            this.delayedShow = true;
        });

        let regions: IPlace[] = [];
        let locations: IPlace[] = [];

        if (this.shouldShowLocationSelector) {
            regions = Object
                .keys(this.geoLocationInfo?.references ?? {})
                .sort((a, b) => a > b ? 1 : -1)
                .map(region => ({
                    label: region,
                    id: region,
                    selected: region === this.selectedRegion,
                }));

            locations = Object
                .keys(this.geoLocationInfo?.references[this.selectedRegion] ?? {})
                .sort((a, b) => a > b ? 1 : -1)
                .map(location => ({
                    label: location,
                    id: location,
                    selected: location === this.selectedLocation,
                }));
        }

        return (
            <div class={`module-modal modal modal-dark modal-geo show ${this.delayedShow && 'animate'}`} data-plugin="moduleModal">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <p class="headline h5 modal-title">
                                {this.dialogTitle}
                            </p>
                        </div>
                        <div class="modal-body">
                            <p style={{'margin-bottom': '1rem'}}>{this.description}</p>

                            {this.shouldShowLocationSelector &&
                                <dc-region-selector
                                    regionLabel={this.regionSelectorLabel}
                                    regionPlaceholder={this.regionSelectorPlaceholder}
                                    regions={regions}

                                    locationLabel={this.locationSelectorLabel}
                                    locationPlaceholder={this.locationSelectorPlaceholder}
                                    locations={locations}
                                ></dc-region-selector>
                            }

                            {this.selectedLocation != this.targetLocation &&
                                <div class="module-callout callout-information has-icon"
                                     data-plugin="moduleCallout"
                                     data-display-time="">
                                    <i class="icon"></i>
                                    <div class="text-wrapper">
                                        <p
                                            class="headline"
                                            innerHTML={this.locationHasNoOwnSiteHeading.replace('{0}', this.selectedLocation)}
                                        ></p>
                                        <p class="paragraph">{this.locationHasNoOwnSiteDescription}</p>
                                    </div>
                                </div>
                            }

                            <div class="module-button-group">
                                <div class="module-button modal-continue">
                                    <button
                                        aria-label={this.finalLocalButtonText}
                                        class="btn btn-primary"
                                        onClick={(event: UIEvent) => this.enterNationalSite(event)}
                                        type="button"
                                    ></button>
                                    <div class="button-animation"></div>
                                    <div class="button-content">
                                        <span class="content-text">{this.finalLocalButtonText}</span>
                                    </div>
                                </div>
                                <div class="module-button ">
                                    <button
                                        aria-label={this.internationalButtonText}
                                        class="btn btn-tertiary"
                                        onClick={(event: UIEvent) => this.stayOnInternationalSite(event)}
                                        type="button"
                                    ></button>
                                    <div class="button-animation"></div>
                                    <div class="button-content">
                                        <span class="content-text">{this.internationalButtonText}</span>
                                    </div>
                                </div>
                            </div>

                            {!this.shouldShowLocationSelector &&
                                <div>
                                    <p style={{'margin-bottom': '1rem', 'margin-top': '1rem'}}>
                                        {this.countrySwitchText}
                                        <a href="#" onClick={(event: UIEvent) => this.showLocationSelector(event)}>
                                            {this.countrySwitchLinkText}
                                        </a>
                                    </p>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Click handler for the modal to manually select a national website
     * Displays two dropdowns for region and country
     * @param event
     * @protected
     */
    protected showLocationSelector(event: UIEvent) {
        event.stopPropagation();
        event.preventDefault();
        this.shouldShowLocationSelector = true;

        const action = this.dialogTitle
            + "<>"
            + this.countrySwitchLinkText;

        window.utag?.link({
            event_action: action.replace(/\s/g, "_").toLowerCase(),
            event_label: '',
            event_subtype: "onpage_flow",
            event_type: "click",
            event_reference: ""
        });
    }

    /**
     * Click handler to enter national site
     * @param _event
     * @protected
     */
    protected enterNationalSite(_event: UIEvent) {
        if (this.shouldShowLocationSelector) {
            if (!this.selectedRegion) {
                alert(this.regionSelectorLabel);
                return;
            }

            if (!this.selectedLocation) {
                alert(this.locationSelectorLabel);
                return;
            }
        }

        const targetURL = this.geoLocationInfo?.references[this.selectedRegion]?.[this.selectedLocation]?.url;

        if (!targetURL) {
            throw new Error(`No location url exists for ${this.selectedRegion}/${this.selectedLocation}!`);
        }

        const action = this.dialogTitle
            + "<>"
            + this.finalLocalButtonText;

        // todo: use central tracking library
        window.utag?.link({
            event_action: action.replace(/\s/g, "_").toLowerCase(),
            event_label: targetURL,
            event_subtype: "countryswitch",
            event_type: "click",
            event_reference: "modal"
        });

        {
            const destinationUrl = new URL(targetURL);

            this.goToLocalModalShareInformation.emit({
                countryOfDestination: getCountryFromUrl(destinationUrl),
                languageOfDestination: getLanguageFromUrl(destinationUrl),
                origin: 'welcome',
            });
        }

        this.rememberModalWasDisplayed();
        setTimeout(() => window.location.assign(targetURL));
    }

    protected rememberModalWasDisplayed() {
        sessionStorage.setItem('dc-redirect-modal-was-displayed', 'true')
    }

    protected setLocation(newLocation?: string) {
        this.selectedLocation = newLocation;
        this.targetLocation = this.geoLocationInfo?.references[this.selectedRegion]?.[this.selectedLocation]?.website;
        this.finalLocalButtonText = this.localButtonText.replace('{0}', this.targetLocation?.toUpperCase() ?? 'YOUR COUNTRY');
    }

    protected setRegion(newRegion?: string) {
        this.setLocation(undefined);
        this.selectedRegion = newRegion;
    }

    /**
     * Check a customer experience object if the modal should be shown
     * @protected
     */
    protected shouldShowModal(): boolean {
        const isCountrySwitchNeeded = this.customerExperienceContext?.countrySwitchIsNeeded ?? this.fetchDone;
        return this.show || (
            isCountrySwitchNeeded && !this.closed && !this.wasModalWasDisplayed()
        );
    }

    /**
     * Click handler for option to stay on international site
     * @param _event
     * @protected
     */
    protected stayOnInternationalSite(_event: UIEvent) {
        this.close(false);

        if (!this.targetLocation) {
            throw new Error(`No website exists for ${this.selectedRegion}/${this.selectedLocation}!`);
        }

        const action = this.dialogTitle
            + "<>"
            + this.internationalButtonText;

        // todo: use central tracking library
        window.utag?.link({
            event_action: action.replace(/\s/g, "_").toLowerCase(),
            event_label: "",
            event_subtype: "onpage_flow",
            event_type: "click",
            event_reference: "modal"
        });

        {
            this.goToLocalModalShareInformation.emit({
                countryOfDestination: getCountryFromUrl(location),
                languageOfDestination: getLanguageFromUrl(location),
                origin: 'welcome',
            });
        }
    }

    protected wasModalWasDisplayed(): boolean {
        return sessionStorage.getItem('dc-redirect-modal-was-displayed') === 'true';
    }
}
